import { extractErrorCode } from '@/api/apiError';
import type { BadgeTone } from '@/ui/kit';
import { MISSING_VALUE } from '@/ui/kit/displayValue';

import { httpStatusOf, messageOf } from '../users/adminErrors';
import type {
  StockBatch,
  StockIntegrityCheck,
  StockLineage,
  StockLineageNode,
  StockMovement,
} from './stockTypes';

/**
 * Mise en forme de la tracabilite du stock, ecrite **hors des composants**.
 *
 * Ce module ne calcule rien que le serveur sache mieux : les quantites, les
 * statuts et les constats viennent de la reconstitution du journal. Il les
 * **formule** (une phrase par constat, un libelle par statut) et en tire les deux
 * seuls chiffres que le serveur ne rend pas — le solde apres chaque mouvement du
 * lot, et le classement conforme / incoherence / non evaluable d'un controle —,
 * chacun par une regle ecrite et testee.
 *
 * Vocabulaire : un constat est une **incoherence de donnees** (le journal n'est
 * pas valide par le serveur), jamais une faute. Aucun terme ne qualifie un parent
 * ni la sante de l'enfant.
 */

// ---------------------------------------------------------------------------
// Nombres et instants
// ---------------------------------------------------------------------------

const MOMENT_FORMAT = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });

/** Instant ISO lisible, ou « — » : jamais « Invalid Date ». */
export const formatMoment = (iso: string | null): string => {
  if (iso === null || iso === '') {
    return MISSING_VALUE;
  }

  const date = new Date(iso);

  return Number.isNaN(date.getTime()) ? MISSING_VALUE : MOMENT_FORMAT.format(date);
};

const NUMBER_FORMAT = new Intl.NumberFormat('fr-FR');

/** Le signe moins typographique, comme les maquettes (« −90 »). */
const MINUS = '−';

const unitWord = (quantity: number, unit: string | null): string => {
  if (unit === 'ml') {
    return ' ml';
  }

  if (unit === 'portion') {
    return Math.abs(quantity) > 1 ? ' portions' : ' portion';
  }

  // Unite inconnue : on n'en invente pas une.
  return '';
};

/** « 90 ml », « 3 portions » ; « — » quand la quantite n'a pas pu etre lue. */
export const formatQuantity = (quantity: number | null, unit: string | null): string =>
  quantity === null ? MISSING_VALUE : `${NUMBER_FORMAT.format(quantity)}${unitWord(quantity, unit)}`;

/** Variation signee d'un mouvement : « +180 ml », « −90 ml », « 0 ml ». */
export const formatDelta = (delta: number | null, unit: string | null): string => {
  if (delta === null) {
    return MISSING_VALUE;
  }

  const sign = delta > 0 ? '+' : delta < 0 ? MINUS : '';

  return `${sign}${NUMBER_FORMAT.format(Math.abs(delta))}${unitWord(delta, unit)}`;
};

/** Huit premiers caracteres d'un identifiant : de quoi le reconnaitre, pas le lire. */
export const shortId = (id: string): string => id.slice(0, 8);

// ---------------------------------------------------------------------------
// Lot
// ---------------------------------------------------------------------------

const STATUS_BADGES: Readonly<Record<string, { readonly label: string; readonly tone: BadgeTone }>> = {
  available: { label: 'Disponible', tone: 'success' },
  split: { label: 'Réparti', tone: 'info' },
  merged: { label: 'Fusionné', tone: 'info' },
  consumed: { label: 'Consommé', tone: 'neutral' },
  archived: { label: 'Archivé', tone: 'neutral' },
};

/** Statut du journal, en toutes lettres ; une valeur inconnue est rendue telle quelle. */
export const describeStatus = (status: string | null): { readonly label: string; readonly tone: BadgeTone } | null => {
  if (status === null || status === '') {
    return null;
  }

  return STATUS_BADGES[status] ?? { label: status, tone: 'neutral' };
};

const CATEGORY_LABELS: Readonly<Record<string, string>> = {
  milk: 'Lait',
  puree: 'Purée de légumes',
  complete_meal: 'Repas complet',
  meat: 'Viande',
  compote: 'Compote',
  other: 'Autre préparation',
};

export const categoryLabel = (category: string | null): string =>
  category === null ? MISSING_VALUE : (CATEGORY_LABELS[category] ?? category);

const STORAGE_TYPE_LABELS: Readonly<Record<string, string>> = {
  refrigerator: 'Réfrigérateur',
  freezer: 'Congélateur',
  room_temperature: 'Température ambiante',
  cooler: 'Glacière',
  thawing: 'Décongélation',
  other: 'Autre lieu',
};

/**
 * Lieu actuel. Le serveur ne rend que le **type** du lieu, pas son nom (c'est un
 * texte de l'utilisateur) : le type est donc ce qu'on affiche, l'identifiant
 * n'etant montre que lorsque le type est inconnu du journal.
 */
export const describeCurrentLocation = (batch: StockBatch): string => {
  if (batch.storageLocationType !== null) {
    return STORAGE_TYPE_LABELS[batch.storageLocationType] ?? batch.storageLocationType;
  }

  return batch.storageLocationId === null ? MISSING_VALUE : `Lieu ${shortId(batch.storageLocationId)}`;
};

/**
 * Lieu cite par un mouvement. Seul le lieu **actuel** du lot a un type connu ;
 * tout autre est designe par le debut de son identifiant, sans en inventer le nom.
 */
export const describeMovementLocation = (locationId: string | null, batch: StockBatch): string => {
  if (locationId === null) {
    return MISSING_VALUE;
  }

  return locationId === batch.storageLocationId ? describeCurrentLocation(batch) : `Lieu ${shortId(locationId)}`;
};

/**
 * Echeance annoncee a cote du statut : un fait simple, `expiresAt` passe ou non.
 * Ce n'est ni une priorite d'usage ni un controle des regles de conservation
 * (reglages personnels ignores du serveur, ADR 0033). Un lot qui n'est plus
 * disponible n'a plus d'echeance qui compte : aucune pastille.
 */
export const describeExpiry = (
  batch: StockBatch,
  now: Date,
): { readonly label: string; readonly tone: BadgeTone } | null => {
  if (batch.status !== 'available' || batch.expiresAt === null) {
    return null;
  }

  const expiresAt = new Date(batch.expiresAt);

  if (Number.isNaN(expiresAt.getTime())) {
    return null;
  }

  return expiresAt.getTime() < now.getTime()
    ? { label: `Expiré le ${formatMoment(batch.expiresAt)}`, tone: 'danger' }
    : { label: `Expire le ${formatMoment(batch.expiresAt)}`, tone: 'neutral' };
};

/** Contenu et poids de portion d'un lot hors lait ; `null` pour le lait. */
export const describePortionContent = (batch: StockBatch): string | null => {
  if (batch.unit !== 'portion') {
    return null;
  }

  const weight =
    batch.portionWeightGrams === null ? null : `${NUMBER_FORMAT.format(batch.portionWeightGrams)} g la portion`;

  return [batch.contentLabel, weight].filter((part): part is string => part !== null && part !== '').join(' · ');
};

// ---------------------------------------------------------------------------
// Mouvements
// ---------------------------------------------------------------------------

const OPERATIONS: Readonly<Record<string, { readonly label: string; readonly tone: BadgeTone }>> = {
  creation: { label: 'Création', tone: 'success' },
  move: { label: 'Déplacement', tone: 'neutral' },
  split: { label: 'Répartition', tone: 'info' },
  merge: { label: 'Fusion', tone: 'info' },
  consumption: { label: 'Consommation', tone: 'brand' },
  loss: { label: 'Perte', tone: 'warning' },
  correction: { label: 'Correction', tone: 'warning' },
};

export const describeOperation = (type: string | null): { readonly label: string; readonly tone: BadgeTone } =>
  type === null ? { label: MISSING_VALUE, tone: 'neutral' } : (OPERATIONS[type] ?? { label: type, tone: 'neutral' });

/** Solde d'un lot apres un mouvement, ou la raison pour laquelle il n'y en a pas. */
export type BalanceCell =
  | { readonly kind: 'value'; readonly value: number }
  | { readonly kind: 'not-evaluable' }
  /** Mouvement d'un autre lot de la genealogie : le solde de CE lot n'a rien a en dire. */
  | { readonly kind: 'other-batch' };

/** Mouvements qui font varier le restant d'un lot : la formule du controle `balance`. */
const BALANCE_MOVEMENTS: readonly string[] = ['consumption', 'loss', 'correction'];

/**
 * Solde du lot apres chacun de **ses** mouvements, dans l'ordre du journal.
 *
 * Meme formule que le controle `balance` du serveur : restant = quantite
 * initiale + somme des variations de consommation, de perte et de correction ;
 * une repartition ramene le lot a zero, et une fusion aussi quand le lot en est une
 * **source** (la fusion s'ecrit une seule fois, sur le lot produit, qui cite ses
 * sources). Un deplacement, la fusion qui produit le lot (dont l'initial compte
 * deja le total) et une creation ne changent pas le restant.
 *
 * **Non evaluable** des qu'un maillon manque — quantite initiale illisible,
 * variation absente, type de mouvement inconnu —, et le reste du lot l'est avec
 * lui : un solde recalcule a partir d'un trou serait un nombre invente.
 *
 * Le tableau rendu est **parallele** a `movements`.
 */
export const movementBalances = (
  batch: StockBatch,
  movements: readonly StockMovement[],
): readonly BalanceCell[] => {
  let running: number | null = batch.initialQuantity;

  return movements.map((movement): BalanceCell => {
    if (movement.batchId !== batch.id) {
      // Une source de fusion n'a aucun mouvement a elle : son statut passe a
      // « fusionne » et le controle du serveur attend alors zero
      // (`TRANSFERRED_STATUSES`). La ligne de la fusion le dit, a condition que la
      // fiche le dise aussi : un lot qui n'est pas « fusionne » ne tombe pas a zero.
      if (movement.type === 'merge' && batch.status === 'merged' && movement.relatedBatchIds.includes(batch.id)) {
        running = 0;

        return { kind: 'value', value: 0 };
      }

      return { kind: 'other-batch' };
    }

    if (movement.type === 'creation') {
      running = batch.initialQuantity;
    } else if (movement.type === 'split') {
      running = 0;
    } else if (movement.type !== null && BALANCE_MOVEMENTS.includes(movement.type)) {
      running = running === null || movement.quantityDelta === null ? null : running + movement.quantityDelta;
    } else if (movement.type !== 'move' && movement.type !== 'merge') {
      // Type absent ou inconnu : on ne sait pas ce qu'il fait au restant.
      running = null;
    }

    return running === null ? { kind: 'not-evaluable' } : { kind: 'value', value: running };
  });
};

// ---------------------------------------------------------------------------
// Genealogie
// ---------------------------------------------------------------------------

const groupByDepth = (nodes: readonly StockLineageNode[]): readonly (readonly StockLineageNode[])[] => {
  const byDepth = new Map<number, StockLineageNode[]>();

  for (const node of nodes) {
    byDepth.set(node.depth, [...(byDepth.get(node.depth) ?? []), node]);
  }

  return [...byDepth.entries()].sort(([a], [b]) => a - b).map(([, group]) => group);
};

/**
 * Generations de la genealogie, de la plus ancienne a la plus recente :
 * ascendants (le plus lointain d'abord), puis descendants (le plus proche d'abord).
 */
export const lineageGenerations = (
  lineage: StockLineage,
): { readonly parents: readonly (readonly StockLineageNode[])[]; readonly children: readonly (readonly StockLineageNode[])[] } => ({
  parents: [...groupByDepth(lineage.parents)].reverse(),
  children: groupByDepth(lineage.children),
});

/** Code de chaque lot connu de la trace, pour nommer les rattachements. */
export const batchCodes = (batch: StockBatch, lineage: StockLineage): ReadonlyMap<string, string | null> =>
  new Map<string, string | null>([
    [batch.id, batch.code],
    ...[...lineage.parents, ...lineage.children].map((node): [string, string | null] => [node.batchId, node.code]),
  ]);

// ---------------------------------------------------------------------------
// Controles d'integrite
// ---------------------------------------------------------------------------

export type CheckOutcome = 'violation' | 'not-evaluable' | 'conforming';

/**
 * Classement d'un controle. **Conforme est le seul etat qui exige tout :** un
 * statut `ok` ET aucun element non evalue. Un `ok` accompagne d'un
 * `notEvaluable` non nul veut dire « rien de faux dans ce qui a pu etre lu » —
 * jamais « tout est conforme » —, et un statut que le serveur ecrirait autrement
 * demain tombe dans « non evaluable », jamais dans « conforme ».
 */
export const checkOutcome = (check: StockIntegrityCheck): CheckOutcome => {
  if (check.status === 'violation' || check.violations.length > 0) {
    return 'violation';
  }

  return check.status === 'ok' && check.notEvaluable === 0 ? 'conforming' : 'not-evaluable';
};

export interface IntegritySummary {
  /** Nombre d'incoherences relevees, tous controles confondus. */
  readonly inconsistencies: number;
  readonly conforming: number;
  readonly notEvaluable: number;
}

export const summarizeIntegrity = (checks: readonly StockIntegrityCheck[]): IntegritySummary => ({
  inconsistencies: checks.reduce((total, check) => total + check.violations.length, 0),
  conforming: checks.filter((check) => checkOutcome(check) === 'conforming').length,
  notEvaluable: checks.filter((check) => checkOutcome(check) === 'not-evaluable').length,
});

const CHECK_TITLES: Readonly<Record<string, string>> = {
  non_negative: 'Aucun restant négatif',
  remaining_le_initial: 'Restant au plus égal à la quantité initiale',
  status_coherent: 'Statut cohérent avec le restant',
  balance: 'Restant = quantité initiale + mouvements',
  split_conservation: 'Répartition : lots enfants = quantité répartie',
  reference_date: 'Date du lait = date de sa source',
  no_refreeze: 'Aucune recongélation après décongélation',
};

export const checkTitle = (code: string): string => CHECK_TITLES[code] ?? code;

const detailNumber = (detail: Readonly<Record<string, unknown>>, key: string): number | null => {
  const value = detail[key];

  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const detailText = (detail: Readonly<Record<string, unknown>>, key: string): string | null => {
  const value = detail[key];

  return typeof value === 'string' && value !== '' ? value : null;
};

const REFERENCE_SOURCES: Readonly<Record<string, string>> = {
  pumping_session: 'la séance de tirage',
  parent: 'le lot parent',
  merge_sources: 'la source la plus ancienne de la fusion',
};

const UNREADABLE_DETAIL = 'Incohérence relevée ; le détail du constat n’a pas pu être lu.';

/**
 * Phrase d'un constat, formulee depuis le detail structure du serveur. Une clef
 * absente ou d'un autre type ne fait pas lever : le constat reste affiche, sans
 * le chiffre qui manque.
 */
export const describeViolation = (code: string, detail: Readonly<Record<string, unknown>>): string => {
  const unit = detailText(detail, 'unit');
  const quantity = (key: string): string => formatQuantity(detailNumber(detail, key), unit);

  switch (code) {
    case 'non_negative':
      return `Restant le plus bas relevé : ${quantity('lowestRemaining')}.`;
    case 'remaining_le_initial':
      return `Restant de ${quantity('remaining')} pour ${quantity('initial')} au départ.`;
    case 'status_coherent':
      return `Statut « ${describeStatus(detailText(detail, 'status'))?.label ?? MISSING_VALUE} » avec ${quantity('remaining')} restants.`;
    case 'balance':
      return `Restant relevé : ${quantity('remaining')} ; restant calculé depuis les mouvements : ${quantity('expected')}.`;
    case 'split_conservation':
      return `Quantité répartie : ${quantity('splitQuantity')} ; somme des lots enfants : ${quantity('childrenInitialTotal')}.`;
    case 'reference_date': {
      const source = REFERENCE_SOURCES[detailText(detail, 'source') ?? ''] ?? 'sa source';

      return `Date du lait : ${formatMoment(detailText(detail, 'referenceAt'))} ; date de ${source} : ${formatMoment(detailText(detail, 'expectedAt'))}.`;
    }
    case 'no_refreeze':
      return `Lot décongelé le ${formatMoment(detailText(detail, 'thawedAt'))}, vu ensuite dans un lieu de type congélateur.`;
    default:
      return UNREADABLE_DETAIL;
  }
};

// ---------------------------------------------------------------------------
// Erreurs
// ---------------------------------------------------------------------------

/**
 * Ce que l'ecran dit d'un echec. Quatre issues, car chacune appelle une reaction
 * differente : un refus de role n'est pas une panne (et ne deconnecte jamais),
 * un lot sans trace n'en est pas une non plus, et seules les vraies pannes
 * proposent « Réessayer ».
 */
export type StockProblem =
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'notice'; readonly title: string; readonly message: string }
  | { readonly kind: 'failure'; readonly message: string };

export const describeStockProblem = (error: unknown): StockProblem => {
  const status = httpStatusOf(error);
  const body: unknown = typeof error === 'object' && error !== null ? (error as { body?: unknown }).body : undefined;
  const code = extractErrorCode(body);

  if (status === 403) {
    return { kind: 'forbidden' };
  }

  if (status === 404) {
    return {
      kind: 'notice',
      title: 'Aucune trace lisible',
      message: 'Ce lot n’a aucun instantané lisible dans ce périmètre du journal.',
    };
  }

  if (status === 410 || code === 'account_anonymized') {
    return {
      kind: 'notice',
      title: 'Compte anonymisé',
      message: 'Plus aucun compte actif n’est rattaché à ce périmètre : rien n’en est affiché.',
    };
  }

  if (code === 'journal_too_large') {
    return {
      kind: 'notice',
      title: 'Journal trop volumineux',
      message:
        'Ce périmètre dépasse 20 000 évènements de stock. Le serveur refuse la reconstitution plutôt que de l’afficher tronquée.',
    };
  }

  if (code === 'too_many_matches') {
    return {
      kind: 'notice',
      title: 'Recherche trop large',
      message: 'Plus de cinq périmètres correspondent à cette valeur. Saisissez le code complet d’un lot.',
    };
  }

  if (status === 422) {
    return {
      kind: 'notice',
      title: 'Valeur non reconnue',
      message:
        'Saisissez un code de lot (MM-AAAAMMJJTHHMMSS-NN-xxxxxx) ou l’identifiant d’un lot, d’une séance de tirage ou d’un biberon.',
    };
  }

  return { kind: 'failure', message: messageOf(error) };
};
